import React, { useState, useMemo, useEffect } from 'react';
import ReactPaginate from 'react-paginate';
import { Modal, Button } from 'react-bootstrap';

interface ColumnConfig<T> {
    key: keyof T;
    label: string;
    render?: (value: any, row: T) => React.ReactNode;
}

interface DataTableProps<T> {
    data: T[];
    onEdit?: (row: T) => void;
    onDelete?: (row: T) => void;
    onRowClick?: (row: T) => void;
    itemsPerPage?: number;
    hiddenFields?: (keyof T)[];
    columns?: ColumnConfig<T>[];

    // NUEVAS OPCIONALES PARA PAGINACIÓN REMOTA
    serverSide?: boolean; // si es true, el backend maneja los datos
    totalItems?: number; // total de registros del backend
    onPageChange?: (page: number, size: number) => void; // callback para pedir datos al backend
}

export function DataTable<T extends { [key: string]: any }>({
    data,
    onEdit,
    onDelete,
    onRowClick,
    itemsPerPage = 10,
    hiddenFields = [],
    columns = [],
    serverSide = false,
    totalItems,
    onPageChange,
}: DataTableProps<T>) {
    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(itemsPerPage);
    const [searchTerm, setSearchTerm] = useState('');

    // Estado del modal de confirmación
    const [showModal, setShowModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState<T | null>(null);

    useEffect(() => {
        if (serverSide && onPageChange) {
            onPageChange(currentPage, rowsPerPage);
        }
    }, [serverSide, currentPage, rowsPerPage]);

    const filteredData = useMemo(() => {
        if (serverSide) return data;
        return data.filter((item) =>
            Object.values(item).some((value) =>
                String(value).toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    }, [searchTerm, data, serverSide]);

    const offset = currentPage * rowsPerPage;
    const currentPageData = serverSide
        ? data
        : filteredData.slice(offset, offset + rowsPerPage);

    const pageCount = serverSide
        ? Math.ceil((totalItems ?? 0) / rowsPerPage)
        : Math.ceil(filteredData.length / rowsPerPage);

    const handlePageClick = ({ selected }: { selected: number }) => {
        setCurrentPage(selected);
    };

    const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setRowsPerPage(Number(e.target.value));
        setCurrentPage(0);
    };

    const keys = useMemo(() => {
        if (columns && columns.length > 0) {
            return columns.filter((col) => !hiddenFields.includes(col.key));
        } else {
            const autoKeys = Object.keys(data[0] || {}) as (keyof T)[];
            return autoKeys
                .filter((key) => !hiddenFields.includes(key))
                .map((key) => ({ key, label: key as string, render: undefined } as ColumnConfig<T>));
        }
    }, [columns, data, hiddenFields]);

    const handleDeleteClick = (e: React.MouseEvent, item: T) => {
        e.stopPropagation();
        setSelectedItem(item);
        setShowModal(true);
    };

    const handleConfirmDelete = () => {
        if (selectedItem && onDelete) {
            onDelete(selectedItem); // Llamamos al padre
        }
        setShowModal(false);
        setSelectedItem(null);
    };

    const handleCancelDelete = () => {
        setShowModal(false);
        setSelectedItem(null);
    };

    return (
        <div>
            {/* Barra de búsqueda y control de filas */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="input-group w-50">
                    <span className="input-group-text">
                        <i className="fa fa-search"></i>
                    </span>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Buscar..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(0);
                        }}
                    />
                </div>
                <div>
                    <label className="me-2">Filas por página:</label>
                    <select
                        className="form-select d-inline-block w-auto"
                        value={rowsPerPage}
                        onChange={handleRowsPerPageChange}
                    >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                </div>
            </div>

            {/* Tabla */}
            {filteredData.length === 0 ? (
                <div className="text-center">No hay datos</div>
            ) : (
                <div className="table-responsive">
                    <table className="table table-hover text-nowrap">
                        <thead>
                            <tr>
                                {keys.map((col, i) => (
                                    <th key={i}>{col.label}</th>
                                ))}
                                <th style={{ width: '100px' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentPageData.map((item, rowIndex) => (
                                <tr
                                    key={rowIndex}
                                    onClick={() => onRowClick?.(item)}
                                    style={{ cursor: onRowClick ? 'pointer' : 'default' }}
                                >
                                    {keys.map((col, i) => (
                                        <td key={i}> {col.render ? col.render(item[col.key], item) : item[col.key]}</td>

                                    ))}
                                    <td>
                                        {/* Botón de editar */}
                                        {onEdit && (
                                            <button
                                                className="btn btn-sm btn-outline-primary me-2"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onEdit(item);
                                                }}
                                            >
                                                <i className="fa fa-edit" />
                                            </button>
                                        )}

                                        {/* Botón de borrar */}
                                        <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={(e) => handleDeleteClick(e, item)}
                                            disabled={!onDelete}
                                        >
                                            <i className="fa fa-trash" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Paginación */}
            {pageCount > 1 && (
                <ReactPaginate
                    previousLabel={<i className="fa fa-chevron-left" />}
                    nextLabel={<i className="fa fa-chevron-right" />}
                    pageCount={pageCount}
                    onPageChange={handlePageClick}
                    containerClassName="pagination justify-content-center"
                    activeClassName="active"
                    pageClassName="page-item"
                    pageLinkClassName="page-link"
                    previousClassName="page-item"
                    previousLinkClassName="page-link"
                    nextClassName="page-item"
                    nextLinkClassName="page-link"
                    breakClassName="page-item"
                    breakLinkClassName="page-link"
                />
            )}

            {/* Modal de confirmación */}
            <Modal show={showModal} onHide={handleCancelDelete} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar eliminación</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedItem ? (
                        <p>
                            ¿Seguro que deseas eliminar el registro seleccionado?
                        </p>
                    ) : (
                        <p>¿Seguro que deseas eliminar este registro?</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCancelDelete}>
                        Cancelar
                    </Button>
                    <Button variant="danger" onClick={handleConfirmDelete}>
                        Eliminar
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
